import { ApplicationRef, ComponentFactoryResolver, inject, Injectable, Injector } from "@angular/core";
import { OperatorFunction, Subject, throwError } from "rxjs";
import { catchError, take, takeUntil, tap } from "rxjs/operators";

import { SnackbarComponent } from "./snackbar/snackbar.component";
import { instanceSymbol, SnackbarRef } from "./snackbar-ref";
import { Closable } from "../modal/closable";
import { closeSymbol } from "../modal/symbols";

interface Options {
	text: string;
	error?: boolean;
	warning?: boolean;
	action?: string;
	autoDismiss?: boolean | number;
}

@Injectable({
	providedIn: "root",
})
export class SnackbarService implements Closable {
	private componentFactoryResolver = inject(ComponentFactoryResolver);
	private injector = inject(Injector);
	private appRef = inject(ApplicationRef);

	private static id = 1;

	private container: HTMLElement;
	private componentRef;
	private timer;
	private error = false;
	private warning = false;
	private hasAction = false;
	private destroy$: Subject<void>;

	show<T>(options: Options): SnackbarRef {
		this.close();

		// Create a new destroy subject
		this.destroy$ = new Subject<void>();

		this.error = options.error;
		this.warning = options.warning;
		// If we have actions, we're using a snack-bar
		this.hasAction = Boolean(options.action);
		const overlay = this.getOverlay();
		const container = this.createToast(overlay);

		const factory = this.componentFactoryResolver.resolveComponentFactory(SnackbarComponent);
		const displayRef = new SnackbarRef<T>(this);

		const injector = Injector.create({
			providers: [
				{
					provide: SnackbarRef,
					useValue: displayRef,
				},
			],
			parent: this.injector,
		});

		this.componentRef = factory.create(injector);
		this.componentRef.instance.text = options.text;

		if (this.hasAction) {
			this.componentRef.instance.actionText = options.action;
		} else {
			// In case there's no action specified, we have a close button
			this.componentRef.instance.closeItem
				.pipe(
					take(1),
					tap(() => this.close()),
					takeUntil(this.destroy$),
				)
				.subscribe();
		}

		displayRef[instanceSymbol] = this.componentRef.instance;

		this.appRef.attachView(this.componentRef.hostView);

		container.appendChild(this._getComponentRootNode(this.componentRef));

		if (options.autoDismiss !== false) {
			window.clearTimeout(this.timer);
			this.timer = window.setTimeout(
				() => this.close(),
				typeof options.autoDismiss === "number" ? options.autoDismiss : 5000,
			);
		}

		return displayRef;
	}

	close() {
		this[closeSymbol]();
	}

	[closeSymbol]() {
		if (this.container) {
			document.body.removeChild(this.container);
			this.container = undefined;
		}

		if (this.componentRef) {
			this.componentRef.destroy();
		}

		// Complete the destroy subject if it exists
		if (this.destroy$) {
			this.destroy$.next();
			this.destroy$.complete();
		}
	}

	showSnackBarError<T>(label: string): OperatorFunction<T, T> {
		return catchError((err: unknown) => {
			this.show({
				error: true,
				text: label,
			});

			return throwError(err);
		});
	}

	showSnackBarSuccess<T>(label: string): OperatorFunction<T, T> {
		return tap(() => {
			this.show({
				error: false,
				text: label,
			});
		});
	}

	private _getComponentRootNode(componentRef): HTMLElement {
		return componentRef.hostView.rootNodes[0] as HTMLElement;
	}

	private createToast(parent: HTMLElement) {
		const container = document.createElement("div");
		container.classList.add("pv-snackbar-overlay__container");
		container.id = `pv-snackbar-overlay__container-${SnackbarService.id++}`;
		parent.appendChild(container);

		return container;
	}

	private getOverlay() {
		if (!this.container) {
			this.container = this.createOverlay();
		}

		return this.container;
	}

	private createOverlay() {
		const overlay = document.createElement("div");
		overlay.classList.add("pv-snackbar-overlay");

		if (this.error) {
			overlay.classList.add("error");
		}

		if (this.warning) {
			overlay.classList.add("warning");
		}

		if (this.hasAction) {
			overlay.classList.add("snackbar");
		}

		document.body.appendChild(overlay);

		return overlay;
	}
}
